import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';

import { LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM } from '@opendoor/auth-fe';
import { setRUMUserProperty } from '@opendoor/observability';
import {
  OdProtosSellCommon_DenialReason as DenialReason,
  OdProtosSellCommon_Product as Product,
} from '__generated__/globalTypes';
import last from 'lodash-es/last';
import { LoadingPage } from 'platform/components/LoadingPage';
import {
  DevError,
  hasPermissionDeniedError,
  isInvalidArgumentError,
  SystemError,
  UnauthenticatedError,
  UnauthorizedError,
} from 'platform/Error';
import { getLoginUrl } from 'platform/loginRedirect';
import navigationHelpers from 'platform/navigationHelpers';
import { useObservability } from 'platform/observability';
import { useNavigate } from 'platform/router/react';
import { parseAddressRoute } from 'platform/routingHelpers';
import type {
  SellerLeadAddressFragment,
  SellerLeadFragment,
} from 'sellDirect/graphql/fragments/__generated__/SellerLeadFragments';
import { useTrackSellerTransactionFirstResult } from 'sellDirect/helpers/useTrackSellerTransactionFirstResult';

import type { SellerCustomerLead } from './useSellerLeadsByCustomer';
import { useSellerLeadsByCustomer } from './useSellerLeadsByCustomer';
import type { SellerTransaction } from './useSellerTransaction';
import { useSellerTransaction } from './useSellerTransaction';

export type SellerLead = Omit<SellerLeadFragment, 'address'> & {
  address: SellerLeadAddressFragment;
};

const isProductQualified = (productOffering: Product, lead?: SellerLead | null): boolean => {
  if (!lead || !lead.productOfferings || lead.productOfferings.length === 0) {
    return false;
  }

  const offering = lead.productOfferings.find(({ product }) => product === productOffering);
  return offering?.denialReason === DenialReason.NO_DENIAL;
};

export type ISellerLeadContext = {
  lead: SellerLead | null;
  sellerTransaction: SellerTransaction | null;
  customerLeads: SellerCustomerLead[];
  sellerInputUUID: string;
  isBuyOnlyCustomer: boolean;
  isOpendoorOfferQualified: boolean;
  loading: boolean;
};

/** @internal */
export const SellerLeadContext = createContext<ISellerLeadContext>({
  lead: null,
  sellerTransaction: null,
  customerLeads: [],
  sellerInputUUID: '',
  isBuyOnlyCustomer: false,
  isOpendoorOfferQualified: false,
  loading: false,
});

/**
 * Returns the latest seller lead for a given customer and address UUID, or the seller lead for a given seller input UUID.
 * Also returns the calculated seller input UUID, customer UUID, address UUID, and experiment entity ID.
 *
 * @throws Error if no leads are found for the customer and feature flag is enabled for buy-only hub routing
 */
export function useSellerLead() {
  const value = useContext(SellerLeadContext);
  if (!value) {
    throw new DevError('SellerLeadContext missing');
  }
  return value;
}

/**
 * SellerLeadProvider is a wrapper component that provides the latest seller lead for a given customer and address UUID.
 *
 * Renders an error page if the customer doesn't have adequate permissions to view the lead.
 * @throws UnauthorizedError if the customer doesn't have adequate permissions to view the lead
 */
export const SellerLeadProvider: FC<PropsWithChildren> = ({ children }) => {
  const { trackEvent } = useObservability();
  const navigate = useNavigate();
  const { addressUUID } = useParams();
  const { pathname } = useLocation();
  const { basePath, subPath } = parseAddressRoute(pathname);

  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get(LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM);

  const sellerLeadsByCustomer = useSellerLeadsByCustomer();
  const sellerTransactionResponse = useSellerTransaction();

  const sellerLeadData = useMemo<ISellerLeadContext>(() => {
    let customerLeads = sellerLeadsByCustomer.customerLeads;
    const lead = sellerTransactionResponse.lead;

    if (lead) {
      // It's possible for Reception, Seller, and Web to be out of sync and the
      // current lead to not be included in the list of seller leads. If that
      // happens, we'll add it into the list ourselves.
      const isCurrentLeadInAllLeads = customerLeads.some(
        (currentLead) => currentLead.address.uuid === lead.address.uuid,
      );
      if (!isCurrentLeadInAllLeads) {
        customerLeads = [...customerLeads, lead];
      }
    }

    const isBuyOnlyCustomer = customerLeads.length === 0;
    const isSellDirectQualified = isProductQualified(Product.OPENDOOR_DIRECT, lead);
    const isSellWithUpsideQualified = isProductQualified(Product.SELL_WITH_UPSIDE, lead);
    const isOpendoorOfferQualified = isSellDirectQualified || isSellWithUpsideQualified;

    return {
      loading: sellerLeadsByCustomer.loading || sellerTransactionResponse.loading,
      lead: lead ?? null,
      sellerTransaction: sellerTransactionResponse.sellerTransaction ?? null,
      customerLeads,
      isBuyOnlyCustomer,
      sellerInputUUID: lead?.source?.id || '',
      isOpendoorOfferQualified,
    };
  }, [
    sellerLeadsByCustomer.loading,
    // eslint-disable-next-line @opendoor/no-unstable-hook-deps -- customerLeads from GraphQL hook result
    sellerLeadsByCustomer.customerLeads,
    sellerTransactionResponse.loading,
    sellerTransactionResponse.lead,
    sellerTransactionResponse.sellerTransaction,
  ]);

  useTrackSellerTransactionFirstResult(addressUUID, sellerTransactionResponse);

  const latestCustomerAddressUUID = last(sellerLeadData.customerLeads)?.address.uuid;

  const missingTransactionWithoutError =
    sellerTransactionResponse.sellerTransaction === null && !sellerTransactionResponse.error;
  const hasInvalidArgumentError =
    sellerTransactionResponse.error && isInvalidArgumentError(sellerTransactionResponse.error);

  // If either of these conditions are true, we will try to redirect to the customer's latest address
  const shouldAttemptLatestLeadAddressRecovery =
    missingTransactionWithoutError || hasInvalidArgumentError;

  // Redirect to latest address if current address is invalid or no address is provided
  useEffect(() => {
    if (sellerLeadData.loading) {
      return;
    }

    if (!addressUUID && latestCustomerAddressUUID) {
      navigate(`${basePath}/${latestCustomerAddressUUID}${subPath}`);
    } else if (sellerLeadData.sellerTransaction == null && addressUUID && accessToken) {
      // If there is no transaction for the current user on the provided address,
      // this means that the current user is logged in to the wrong human.
      // Thus we redirect to the login lite url (opendoor.com/token) to log out of the
      // current (wrong) human, and log into the right one using the access_token
      navigationHelpers.hardNavReplace(getLoginUrl(window.location.href));
    } else if (
      shouldAttemptLatestLeadAddressRecovery &&
      latestCustomerAddressUUID &&
      addressUUID !== latestCustomerAddressUUID
    ) {
      navigate(`${basePath}/${latestCustomerAddressUUID}${subPath}`);
    }
  }, [
    basePath,
    subPath,
    navigate,
    latestCustomerAddressUUID,
    addressUUID,
    sellerLeadData.loading,
    sellerLeadData.sellerTransaction,
    shouldAttemptLatestLeadAddressRecovery,
    accessToken,
  ]);

  /**
   * This happens sometimes but shouldn't block the rendering of the UI. Just tracking it so we can monitor the frequency for now
   */
  useEffect(() => {
    if (
      !sellerLeadData.loading &&
      sellerLeadData.lead &&
      (!sellerLeadData.sellerInputUUID || sellerLeadData.sellerInputUUID.trim() === '')
    ) {
      trackEvent('missing-seller-input');
    }
  }, [sellerLeadData.lead, sellerLeadData.loading, sellerLeadData.sellerInputUUID, trackEvent]);

  useEffect(() => {
    if (sellerLeadData?.sellerInputUUID) {
      setRUMUserProperty({ sellerInputUUID: sellerLeadData.sellerInputUUID });
    }
  }, [sellerLeadData.sellerInputUUID]);

  if (!sellerLeadData.loading) {
    if (sellerTransactionResponse.error) {
      if (
        sellerTransactionResponse.error instanceof UnauthenticatedError ||
        sellerTransactionResponse.error instanceof UnauthorizedError
      ) {
        throw sellerTransactionResponse.error;
      }

      if (hasPermissionDeniedError(sellerTransactionResponse.error)) {
        throw new UnauthorizedError('LatestSellerLead', 'permission-denied', 'seller');
      }

      if (shouldAttemptLatestLeadAddressRecovery) {
        // If no fallback address exists, or the current address IS the fallback, throw error
        if (!latestCustomerAddressUUID || addressUUID === latestCustomerAddressUUID) {
          throw new SystemError(sellerTransactionResponse.error, 'seller');
        }

        // Show loading while redirect effect (above) navigates to fallback address
        return <LoadingPage />;
      }

      throw new SystemError(sellerTransactionResponse.error, 'seller');
    }

    // Show loading while redirect effect navigates to the latest address.
    // If the UUID is already in the URL but useParams() still doesn't have it, the route
    // is missing the :addressUUID param — that's a routing misconfiguration.
    if (!addressUUID && latestCustomerAddressUUID) {
      if (pathname.includes(latestCustomerAddressUUID)) {
        throw new DevError('SellerLeadProvider mounted on route without :addressUUID param');
      }
      return <LoadingPage />;
    }

    // Show loading while redirect effect navigates to the login lite page.
    if (sellerLeadData.sellerTransaction == null && addressUUID && accessToken) {
      return <LoadingPage />;
    }

    if (sellerLeadData.lead && !sellerLeadData.lead.address?.uuid) {
      throw new SystemError('hub-route-lead-missing-address-uuid', 'seller');
    }

    // avoid directing the customer to the buy only experience if we fail to load customer leads
    if (sellerLeadsByCustomer.error) {
      if (hasPermissionDeniedError(sellerLeadsByCustomer.error)) {
        throw new UnauthorizedError('ListLeadsByCustomer', 'permission-denied', 'seller');
      }
      throw new SystemError(sellerLeadsByCustomer.error, 'seller');
    }
  }

  return <SellerLeadContext.Provider value={sellerLeadData}>{children}</SellerLeadContext.Provider>;
};
